import ProjectCard from "../components/common/ProjectCard";
import ResultCard from "../components/results/ResultCard";
import CommunityCard from "../components/communities/CommunityCard";
import { Bars } from "react-loader-spinner";
import Container from "../components/common/Container";
import {
  useGetUserByIdQuery,
  useGetUserQuery,
} from "../redux/slices/api/userApi";
import { Link, useSearchParams } from "react-router-dom";
import useSendEmailVerification from "../hooks/useSendEmailVerification";
import ViewOrgProfileLayout from "./org-profile/ViewOrgProfileLayout";
import ViewApplicantProfileLayout from "./user-profile/ViewApplicantProfileLayout";
import useHandleCreateProject from "../hooks/useHandleCreateProject";
import UserPlaceHolderImg from "../assets/img/common/user-placeholder-img.png";
import DefaultHeaderImg from "../assets/img/common/profile-banner.png";
import { publicAddress } from "../utils/localData";
import { BiEditAlt } from "react-icons/bi";
import { useMemo, useState } from "react";
import { useScrollToHashElement } from "../hooks/useScrollToHashElement";
import MetaTags from "../components/common/MetaTags";
import {
  selectGeneral,
  setShowFullscreenImage,
} from "../redux/slices/generalSlice";
import FullScreenImage from "../components/common/FullscreenImage";
import { useDispatch, useSelector } from "react-redux";
import { HiOutlineLocationMarker, HiOutlineDocumentText, HiOutlineUserGroup, HiOutlineCollection, HiOutlineViewGrid, HiOutlineBeaker } from "react-icons/hi";
import VerifiedBadges from "../components/profile/VerifiedBadges";
import DeleteProfileModal from "../components/profile/DeleteProfileModal";
import { getChain } from "../utils/currencies";

const ViewProfile = () => {
  // Retrieving the "id" parameter from the URL search parameters
  let [searchParams] = useSearchParams();
  let userId = searchParams.get("id");
  
  // State for delete modal
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  
  // State for portfolio tab filter
  const [portfolioFilter, setPortfolioFilter] = useState("all");

  // Querying user data by user ID and checking if loading
  const { isLoading: userByIdLoading, data: userByIdData } =
    useGetUserByIdQuery(userId, {
      skip: !userId,
    });

  // Querying current user data and checking if loading
  const { isLoading: meLoading, data: meData } = useGetUserQuery(null, {
    skip: !!userId,
    refetchOnMountOrArgChange: true,
  });

  // Determining the user profile data based on the user ID and current user data
  let { user: userProfileData, projects, results, communities, collectedNFTs } = useMemo(() => {
    let userId = searchParams.get("id");
    if (userId && userByIdData) return userByIdData;
    else if (meData) return meData;
    return {};
  }, [userByIdData, meData, searchParams]);

  // Calculate portfolio items with type badges
  const portfolioItems = useMemo(() => {
    const items = [];
    
    // Add projects with type
    projects?.forEach(project => {
      items.push({ ...project, itemType: 'project' });
    });
    
    // Add results with type
    results?.forEach(result => {
      items.push({ ...result, itemType: 'result' });
    });
    
    // Add communities with type
    communities?.forEach(community => {
      items.push({ ...community, itemType: 'community' });
    });
    
    return items;
  }, [projects, results, communities]);

  // Filter portfolio items based on selected filter
  const filteredPortfolioItems = useMemo(() => {
    if (portfolioFilter === "all") return portfolioItems;
    return portfolioItems.filter(item => item.itemType === portfolioFilter);
  }, [portfolioItems, portfolioFilter]);

  // Calculate counts for filters
  const projectsCount = projects?.length || 0;
  const resultsCount = results?.length || 0;
  const communitiesCount = communities?.length || 0;
  const totalPortfolioCount = projectsCount + resultsCount + communitiesCount;

  // Handling email verification and checking if email sending is in progress
  const { handleSendVerifyEmail, sendEmailLoading } =
    useSendEmailVerification();

  // Handling the creation of a new project
  const { handleCreateProject } = useHandleCreateProject();
  useScrollToHashElement({ isLoading: meLoading || userByIdLoading });

  const { showFullscreenImage } = useSelector(selectGeneral);

  const dispatch = useDispatch();

  // /////////////
  if (userByIdLoading || meLoading) {
    return (
      <div className="w-full min-h-screen flex justify-center items-center bg-gradient-to-b from-whitesmoke to-white">
        <Bars
          height="50"
          width="50"
          color="#8538ff"
          ariaLabel="bars-loading"
          wrapperStyle={{}}
          wrapperClass=""
          visible={true}
        />
      </div>
    );
  } else
    return (
      <div className="w-full flex flex-col items-center bg-gradient-to-b from-whitesmoke via-white to-whitesmoke min-h-screen">
        <MetaTags
          title={"Profile"}
          description={`NFT- Fundraising By FunDeSci An innovative approach for decentralized crowdfunding of scientific projects`}
          link={userByIdData ? `/profile?id=${userId}` : `/profile`}
          imageCard={
            userByIdData ? userProfileData?.images?.profile : UserPlaceHolderImg
          }
        />
        
        {/* Hero Banner Section */}
        <div className="w-full relative">
          {/* Banner Image */}
          <div
            className="w-full h-48 sm:h-56 md:h-72 lg:h-80 bg-cover bg-center relative"
            style={{
              backgroundImage: `url(${
                userProfileData?.images?.header || DefaultHeaderImg
              })`,
            }}
          >
            {/* Gradient Overlays for better text visibility */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent"></div>
            {/* Strong top gradient for navbar visibility */}
            <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/40 to-transparent h-48"></div>
          </div>
          
          {/* Profile Card - Overlapping Banner */}
          <Container className="relative -mt-24 sm:-mt-28 md:-mt-32 z-10">
            <div className="bg-white rounded-2xl sm:rounded-3xl shadow-xl p-4 sm:p-6 md:p-8">
              <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 md:gap-8 items-start">
                {/* Profile Image */}
                <div className="relative -mt-16 sm:-mt-20 md:-mt-24 mx-auto sm:mx-0">
                  <div className="w-28 h-28 sm:w-32 sm:h-32 md:w-40 md:h-40 rounded-2xl sm:rounded-3xl overflow-hidden shadow-xl bg-white">
                    <img
                      onClick={() => dispatch(setShowFullscreenImage(true))}
                      crossOrigin="anonymous"
                      src={userProfileData?.images?.profile || UserPlaceHolderImg}
                      alt="Profile"
                      className="w-full h-full object-cover cursor-pointer hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                </div>
                
                {/* Basic Info */}
                <div className="flex-1 text-center sm:text-left pt-2 sm:pt-4">
                  <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-secondary abel-font">
                    {userProfileData?.role === "applicant"
                      ? `${userProfileData?.firstname || ''} ${userProfileData?.lastname || ''}`
                      : userProfileData?.fundername || 'User'}
                  </h1>
                  
                  {userProfileData?.title_rank && (
                    <p className="text-base sm:text-lg text-primary font-medium mt-1">
                      {userProfileData?.title_rank}
                    </p>
                  )}
                  
                  {/* Location */}
                  {userProfileData?.location && (
                    <div className="flex items-center justify-center sm:justify-start gap-1.5 mt-2 text-gray-500">
                      <HiOutlineLocationMarker className="text-lg" />
                      <span className="text-sm sm:text-base">
                        {userProfileData.location}
                      </span>
                    </div>
                  )}
                  
                  {/* Nationality (if no location but has nationality) */}
                  {!userProfileData?.location && userProfileData?.nationality && (
                    <div className="flex items-center justify-center sm:justify-start gap-1.5 mt-2 text-gray-500">
                      <HiOutlineLocationMarker className="text-lg" />
                      <span className="text-sm sm:text-base">
                        {userProfileData.nationality}
                      </span>
                    </div>
                  )}
                  
                  {/* Verified Account Badges */}
                  <div className="flex justify-center sm:justify-start">
                    <VerifiedBadges verifiedAccounts={userProfileData?.verified_accounts} />
                  </div>
                  
                  {/* Skills/Tags */}
                  {userProfileData?.skills?.length > 0 && (
                    <div className="flex flex-wrap justify-center sm:justify-start gap-2 mt-4">
                      {userProfileData?.skills?.slice(0, 5).map((skill, index) => (
                        <span
                          key={skill?.id || index}
                          className="px-3 py-1 text-xs sm:text-sm bg-gradient-to-r from-primary/10 to-[#7e7cff]/10 text-primary rounded-full font-medium"
                        >
                          {skill?.text}
                        </span>
                      ))}
                      {userProfileData?.skills?.length > 5 && (
                        <span className="px-3 py-1 text-xs sm:text-sm bg-gray-100 text-gray-600 rounded-full font-medium">
                          +{userProfileData?.skills?.length - 5} more
                        </span>
                      )}
                    </div>
                  )}
                </div>
                
                {/* Edit Button - Desktop */}
                {!userId && (
                  <div className="hidden sm:block">
                    <Link
                      to="/create-profile?edit=true"
                      className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-primary to-[#7e7cff] text-white rounded-xl font-medium shadow-lg hover:shadow-primary/30 transition-all duration-300 hover:scale-105 text-sm"
                    >
                      <BiEditAlt size={18} />
                      <span>Edit Profile</span>
                    </Link>
                  </div>
                )}
              </div>
            </div>
          </Container>
        </div>
        
        {/* Main Content */}
        <Container className="w-full flex flex-col mt-6 sm:mt-8">
          {userProfileData?.role === "applicant" ? (
            <ViewApplicantProfileLayout
              handleSendVerifyEmail={handleSendVerifyEmail}
              sendEmailLoading={sendEmailLoading}
              userId={userId}
              userProfileData={userProfileData}
              onDeleteAccount={!userId ? () => setShowDeleteModal(true) : undefined}
            />
          ) : (
            <ViewOrgProfileLayout
              handleSendVerifyEmail={handleSendVerifyEmail}
              sendEmailLoading={sendEmailLoading}
              userId={userId}
              userProfileData={userProfileData}
              onDeleteAccount={!userId ? () => setShowDeleteModal(true) : undefined}
            />
          )}
        </Container>
        
        {/* Portfolio Section */}
        <div className="w-full flex flex-col items-center mt-12 sm:mt-16">
          <div
            id="portfolio"
            className="w-full flex flex-col py-16 sm:py-20 bg-gradient-to-b from-secondary via-[#1a1230] to-secondary relative overflow-hidden"
          >
            {/* Decorative Elements */}
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-primary/10 via-transparent to-transparent pointer-events-none"></div>
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-primary/30 to-transparent"></div>
            
            <Container className="relative z-10">
              {/* Section Header */}
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
                <div>
                  <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-md border border-white/10 text-primary-green roboto-font text-sm font-medium mb-4">
                    Portfolio
                  </span>
                  <h2 className="text-3xl sm:text-4xl lg:text-5xl abel-font text-white leading-tight">
                    {userId ? "Contributions" : "My Contributions"}
                  </h2>
                </div>
                
                {!userId && totalPortfolioCount > 0 && (
                  <button
                    onClick={handleCreateProject}
                    className="px-6 py-3 bg-gradient-to-r from-primary to-[#7e7cff] text-white rounded-xl font-medium shadow-lg hover:shadow-primary/30 transition-all duration-300 hover:scale-105"
                  >
                    + New Project
                  </button>
                )}
              </div>
              
              {/* Portfolio Filter Tabs */}
              <div className="flex flex-wrap gap-2 mb-8">
                <button
                  onClick={() => setPortfolioFilter("all")}
                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                    portfolioFilter === "all"
                      ? "bg-primary text-white shadow-lg shadow-primary/30"
                      : "bg-white/10 text-white/70 hover:bg-white/20 hover:text-white"
                  }`}
                >
                  <HiOutlineViewGrid size={16} />
                  All ({totalPortfolioCount})
                </button>
                <button
                  onClick={() => setPortfolioFilter("project")}
                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                    portfolioFilter === "project"
                      ? "bg-primary text-white shadow-lg shadow-primary/30"
                      : "bg-white/10 text-white/70 hover:bg-white/20 hover:text-white"
                  }`}
                >
                  <HiOutlineBeaker size={16} />
                  Projects ({projectsCount})
                </button>
                <button
                  onClick={() => setPortfolioFilter("result")}
                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                    portfolioFilter === "result"
                      ? "bg-primary text-white shadow-lg shadow-primary/30"
                      : "bg-white/10 text-white/70 hover:bg-white/20 hover:text-white"
                  }`}
                >
                  <HiOutlineDocumentText size={16} />
                  Results ({resultsCount})
                </button>
                <button
                  onClick={() => setPortfolioFilter("community")}
                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                    portfolioFilter === "community"
                      ? "bg-primary text-white shadow-lg shadow-primary/30"
                      : "bg-white/10 text-white/70 hover:bg-white/20 hover:text-white"
                  }`}
                >
                  <HiOutlineUserGroup size={16} />
                  Communities ({communitiesCount})
                </button>
              </div>
              
              {filteredPortfolioItems?.length ? (
                <div className="w-full cards-container justify-items-center gap-y-8 gap-x-4 md:gap-x-6 lg:gap-x-4 xl:gap-x-6">
                  {filteredPortfolioItems?.map((item, index) => {
                    if (item.itemType === "project") {
                      return (
                        <ProjectCard
                          key={`project-${index}`}
                          data={item}
                          isMyProject={
                            publicAddress() === item?.creator?.public_address
                          }
                        />
                      );
                    } else if (item.itemType === "result") {
                      return (
                        <ResultCard
                          key={`result-${index}`}
                          resultData={item}
                          projectData={item?.project}
                        />
                      );
                    } else if (item.itemType === "community") {
                      return (
                        <CommunityCard
                          key={`community-${index}`}
                          data={item}
                        />
                      );
                    }
                    return null;
                  })}
                </div>
              ) : (
                <div className="w-full flex flex-col items-center py-16 sm:py-24">
                  <div className="w-20 h-20 rounded-full bg-gradient-to-br from-primary/20 to-primary-green/20 flex items-center justify-center mb-6">
                    <svg className="w-10 h-10 text-white/40" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                    </svg>
                  </div>
                  {userId ? (
                    <p className="text-xl sm:text-2xl font-semibold text-white/80">
                      This user has no portfolio items yet.
                    </p>
                  ) : (
                    <>
                      <p className="text-xl sm:text-2xl font-semibold text-white/80 mb-6">
                        You don't have any portfolio items yet
                      </p>
                      <button
                        onClick={handleCreateProject}
                        className="px-8 py-4 bg-gradient-to-r from-primary to-[#7e7cff] text-white rounded-xl font-semibold shadow-lg hover:shadow-primary/30 transition-all duration-300 hover:scale-105"
                      >
                        Create Your First Project
                      </button>
                    </>
                  )}
                </div>
              )}
            </Container>
          </div>
        </div>
        
        {/* Collected NFTs Section */}
        {collectedNFTs && (collectedNFTs.funds?.length > 0 || collectedNFTs.memberships?.length > 0) && (
          <div className="w-full flex flex-col items-center">
            <div
              id="collected-nfts"
              className="w-full flex flex-col py-16 sm:py-20 bg-gradient-to-b from-secondary via-[#0f0a1f] to-secondary relative overflow-hidden"
            >
              {/* Decorative Elements */}
              <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom,_var(--tw-gradient-stops))] from-primary-green/10 via-transparent to-transparent pointer-events-none"></div>
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-primary-green/30 to-transparent"></div>
              
              <Container className="relative z-10">
                {/* Section Header */}
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-10">
                  <div>
                    <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-md border border-white/10 text-primary-green roboto-font text-sm font-medium mb-4">
                      <HiOutlineCollection size={16} />
                      NFT Collection
                    </span>
                    <h2 className="text-3xl sm:text-4xl lg:text-5xl abel-font text-white leading-tight">
                      {userId ? "Collected NFTs" : "My Collected NFTs"}
                    </h2>
                  </div>
                </div>
                
                {/* Funded Projects */}
                {collectedNFTs.funds?.length > 0 && (
                  <div className="mb-12">
                    <h3 className="text-xl sm:text-2xl abel-font text-white/90 mb-6 flex items-center gap-2">
                      <span className="w-8 h-8 rounded-lg bg-primary/20 flex items-center justify-center">
                        <HiOutlineBeaker size={18} className="text-primary" />
                      </span>
                      Funded Projects
                      <span className="ml-2 px-2.5 py-0.5 text-sm bg-white/10 rounded-full text-white/60">
                        {collectedNFTs.funds.length}
                      </span>
                    </h3>
                    <div className="w-full cards-container justify-items-center gap-y-6 gap-x-4">
                      {collectedNFTs.funds.map((fund, index) => (
                        <div 
                          key={`fund-${index}`}
                          className="w-full max-w-sm bg-white/5 backdrop-blur-md rounded-2xl border border-white/10 overflow-hidden hover:border-primary/30 transition-all duration-300 group"
                        >
                          <div className="relative h-40 overflow-hidden">
                            <img 
                              src={fund.project?.feature_image} 
                              alt={fund.project?.title}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                            />
                            <div className="absolute top-3 right-3 px-2 py-1 bg-black/60 backdrop-blur-sm rounded-lg text-xs text-white/80 flex items-center gap-1">
                              <img src={getChain({ network: fund.blockchain })?.icon} alt="" className="w-4 h-4" />
                              {getChain({ network: fund.blockchain })?.name}
                            </div>
                          </div>
                          <div className="p-4">
                            <p className="text-sm text-primary-green font-medium mb-1">Funding NFT #{fund.tokenId}</p>
                            <h4 className="text-lg font-semibold text-white mb-2 line-clamp-1">{fund.project?.title}</h4>
                            <div className="flex items-center justify-between text-sm">
                              <span className="text-white/60">Amount</span>
                              <span className="text-white font-medium">{fund.amount} {fund.token}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Community Memberships */}
                {collectedNFTs.memberships?.length > 0 && (
                  <div>
                    <h3 className="text-xl sm:text-2xl abel-font text-white/90 mb-6 flex items-center gap-2">
                      <span className="w-8 h-8 rounded-lg bg-primary-green/20 flex items-center justify-center">
                        <HiOutlineUserGroup size={18} className="text-primary-green" />
                      </span>
                      Community Memberships
                      <span className="ml-2 px-2.5 py-0.5 text-sm bg-white/10 rounded-full text-white/60">
                        {collectedNFTs.memberships.length}
                      </span>
                    </h3>
                    <div className="w-full cards-container justify-items-center gap-y-6 gap-x-4">
                      {collectedNFTs.memberships.map((membership, index) => (
                        <div 
                          key={`membership-${index}`}
                          className="w-full max-w-sm bg-white/5 backdrop-blur-md rounded-2xl border border-white/10 overflow-hidden hover:border-primary-green/30 transition-all duration-300 group"
                        >
                          <div className="relative h-40 overflow-hidden">
                            <img 
                              src={membership.artwork} 
                              alt={membership.name}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                            />
                            <div className="absolute top-3 left-3 px-2.5 py-1 bg-primary-green/90 backdrop-blur-sm rounded-lg text-xs text-white font-medium">
                              Member
                            </div>
                            <div className="absolute top-3 right-3 px-2 py-1 bg-black/60 backdrop-blur-sm rounded-lg text-xs text-white/80 flex items-center gap-1">
                              <img src={getChain({ network: membership.blockchain })?.icon} alt="" className="w-4 h-4" />
                              {getChain({ network: membership.blockchain })?.name}
                            </div>
                          </div>
                          <div className="p-4">
                            <p className="text-sm text-primary-green font-medium mb-1">Community Membership</p>
                            <h4 className="text-lg font-semibold text-white mb-2 line-clamp-1">{membership.name}</h4>
                            <p className="text-sm text-white/60 line-clamp-1">{membership.category}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </Container>
            </div>
          </div>
        )}
        
        {/* Edit Profile Button - Mobile Fixed Bottom */}
        {!userId && (
          <div className="fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur-md border-t border-gray-200 shadow-2xl z-40 sm:hidden">
            <div className="px-4 py-3">
              <Link
                to="/create-profile?edit=true"
                className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-primary to-[#7e7cff] text-white rounded-xl font-semibold shadow-lg"
              >
                <BiEditAlt size={20} />
                <span>Edit Profile</span>
              </Link>
            </div>
          </div>
        )}
        
        {/* Add padding at bottom for mobile edit button */}
        {!userId && <div className="h-20 sm:hidden"></div>}
        
        {showFullscreenImage && (
          <FullScreenImage
            img={userProfileData?.images?.profile || UserPlaceHolderImg}
          />
        )}
        
        {/* Delete Profile Modal */}
        <DeleteProfileModal 
          isOpen={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
        />
      </div>
    );
};

export default ViewProfile;
